/**
 * Renders Handlebars value tokens as friendly, atomic "chips" inside the visual
 * editor, and converts them back to plain Handlebars on the way out.
 *
 * In the raw template a field looks like `{{client.name}}`. Shown verbatim in
 * the canvas that reads nothing like the finished document and is awkward to
 * select or delete (the braces, dots and the text are all separately editable).
 * Instead we display each simple value token as a single non-editable pill
 * labelled "Client Name". The pill:
 *   - is one unit to the caret, so Backspace removes the whole field at once;
 *   - carries its exact Handlebars source in a data attribute, so saving
 *     restores `{{client.name}}` with no guesswork;
 *   - can be clicked to select (used by the floating block/field toolbar).
 *
 * Only SIMPLE VALUE tokens are chipped — `{{x}}`, `{{x.y}}`, `{{helper x}}`.
 * Block tags (`{{#each}}`, `{{#if}}`, `{{/each}}`, `{{else}}`) and comments are
 * left untouched so loops and conditionals keep working; table-loop protection
 * (protectTableTags) handles those separately.
 */

/** A label lookup: Handlebars inner expression -> human label. */
export type TokenLabeler = (expression: string) => string;

/** Marks an element we created so we can find and unwrap our own chips only. */
export const CHIP_CLASS = 'tpl-token-chip';
const CHIP_ATTR = 'data-token';

// {{ ... }} that is NOT a block/else/comment tag. Group 1 is the inner text.
const VALUE_TOKEN_RE = /\{\{\s*(?![#/^!]|else\b)([^{}]+?)\s*\}\}/g;
// Our own chip span, for the reverse direction. `[\s\S]` to span newlines.
const CHIP_RE = new RegExp(`<span[^>]*\\b${CHIP_ATTR}="([^"]*)"[^>]*>[\\s\\S]*?<\\/span>`, 'g');

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function decodeHtml(value: string): string {
  return value
    .replace(/&quot;/g, '"')
    .replace(/&gt;/g, '>')
    .replace(/&lt;/g, '<')
    .replace(/&amp;/g, '&');
}

/** Default label when no labeler maps the expression: a Title Cased leaf name. */
function fallbackLabel(expression: string): string {
  const leaf =
    expression
      .trim()
      .split(/[\s.]+/)
      .pop() ?? expression;
  return leaf
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/**
 * Replaces simple value tokens with chip spans for display in the canvas.
 * `contenteditable="false"` makes each chip atomic to the caret.
 */
export function tokensToChips(html: string, labeler?: TokenLabeler): string {
  if (!html || !html.includes('{{')) return html;
  return html.replace(VALUE_TOKEN_RE, (_match, inner: string) => {
    const expression = inner.trim();
    const label = (labeler?.(expression) || '').trim() || fallbackLabel(expression);
    const token = escapeHtml(`{{${expression}}}`);
    return (
      `<span class="${CHIP_CLASS}" ${CHIP_ATTR}="${token}" contenteditable="false" ` +
      `title="${token}">${escapeHtml(label)}</span>`
    );
  });
}

/** Converts chip spans back to the plain Handlebars tokens they represent. */
export function chipsToTokens(html: string): string {
  if (!html || !html.includes(CHIP_ATTR)) return html;
  return html.replace(CHIP_RE, (_match, token: string) => decodeHtml(token));
}

// ─── Conditional blocks ({{#if}} / {{#unless}} / {{else}} / {{/…}}) ──────────
//
// A conditional in the raw template looks like:
//   {{#if client.business_name}}<p>…</p>{{/if}}
// Left as raw text in the canvas that is three loose, separately-deletable
// pieces (open brace, body, close brace) — deleting the close breaks the block,
// deleting the body leaves an empty condition. Instead we wrap the whole thing
// in one labelled box: a non-editable header ("Only show if: Client Business
// Name") plus the still-editable body (and optional else body). The box carries
// its exact open/else/close tokens in data attributes, so it maps back to
// Handlebars byte-for-byte, and the floating toolbar can delete it as a unit.

export const COND_CLASS = 'tpl-cond-block';
export const COND_BODY_CLASS = 'tpl-cond-body';
const COND_OPEN_ATTR = 'data-cond-open';
const COND_ELSE_ATTR = 'data-cond-else';
const COND_CLOSE_ATTR = 'data-cond-close';

type Segment =
  | { kind: 'text'; html: string }
  | { kind: 'open'; helper: string; expr: string; raw: string }
  | { kind: 'else'; raw: string }
  | { kind: 'close'; raw: string };

/** Splits HTML into text runs and the if/unless/else/close tokens between them. */
function tokenizeConditionals(html: string): Segment[] {
  const re = /\{\{#(if|unless)\s+([^{}]+?)\}\}|\{\{\s*else\s*\}\}|\{\{\/(if|unless)\}\}/g;
  const out: Segment[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html))) {
    if (m.index > last) out.push({ kind: 'text', html: html.slice(last, m.index) });
    if (m[1]) out.push({ kind: 'open', helper: m[1], expr: m[2].trim(), raw: m[0] });
    else if (m[3]) out.push({ kind: 'close', raw: m[0] });
    else out.push({ kind: 'else', raw: m[0] });
    last = m.index + m[0].length;
  }
  if (last < html.length) out.push({ kind: 'text', html: html.slice(last) });
  return out;
}

/**
 * Wraps `{{#if}}` / `{{#unless}}` conditionals in a labelled, editable box.
 * Applied AFTER value tokens are chipped, so the expression and body chips are
 * already in place. Unmatched tokens are left as-is (a half-written block in the
 * code editor shouldn't blow up the canvas).
 */
export function conditionalsToBlocks(html: string, labeler?: TokenLabeler): string {
  if (!html || !/\{\{#(if|unless)\b/.test(html)) return html;

  const segments = tokenizeConditionals(html);
  let out = '';
  // Stack of open conditionals we're currently inside.
  const stack: Array<{ helper: string; expr: string; raw: string; sawElse: boolean }> = [];

  for (const seg of segments) {
    if (seg.kind === 'open') {
      stack.push({ helper: seg.helper, expr: seg.expr, raw: seg.raw, sawElse: false });
      // The condition itself is understood from context, so no visible caption:
      // just a wrapper that keeps the block as one deletable unit. A tooltip
      // carries the human-readable condition for anyone who hovers.
      const label = (labeler?.(seg.expr) || '').trim() || fallbackLabel(seg.expr);
      const verb = seg.helper === 'unless' ? 'Only hidden if' : 'Only shown if';
      out +=
        `<div class="${COND_CLASS}" ${COND_OPEN_ATTR}="${escapeHtml(seg.raw)}" ` +
        `title="${escapeHtml(verb)}: ${escapeHtml(label)}" contenteditable="false">` +
        `<div class="${COND_BODY_CLASS}" contenteditable="true">`;
    } else if (seg.kind === 'else' && stack.length > 0) {
      const top = stack[stack.length - 1];
      top.sawElse = true;
      // A thin "otherwise" divider marks where the else-branch starts; without
      // it the two branches would be visually indistinguishable.
      out +=
        `</div>` +
        `<div class="tpl-cond-else" ${COND_ELSE_ATTR}="${escapeHtml(seg.raw)}" contenteditable="false"></div>` +
        `<div class="${COND_BODY_CLASS}" contenteditable="true">`;
    } else if (seg.kind === 'close' && stack.length > 0) {
      stack.pop();
      out += `</div><div class="tpl-cond-close" ${COND_CLOSE_ATTR}="${escapeHtml(seg.raw)}" contenteditable="false"></div></div>`;
    } else {
      out += seg.kind === 'text' ? seg.html : seg.raw;
    }
  }
  // Any still-open blocks were unmatched: nothing to close, already emitted.
  return out;
}

// A conditional wrapper, captured from its opening tag to the matching close
// of its own class. Non-greedy body; `[\s\S]` to span newlines.
const COND_BLOCK_RE = new RegExp(
  `<div[^>]*\\b${COND_OPEN_ATTR}="([^"]*)"[^>]*>([\\s\\S]*?)<div[^>]*\\b${COND_CLOSE_ATTR}="([^"]*)"[^>]*>\\s*<\\/div>\\s*<\\/div>`,
  'g',
);
const COND_ELSE_DIV_RE = new RegExp(
  `<div[^>]*\\b${COND_ELSE_ATTR}="([^"]*)"[^>]*>[\\s\\S]*?<\\/div>`,
  'g',
);
const COND_LABEL_DIV_RE = /<div[^>]*class="tpl-cond-label"[^>]*>[\s\S]*?<\/div>/g;
const COND_BODY_OPEN_RE = new RegExp(`<div[^>]*class="${COND_BODY_CLASS}"[^>]*>`, 'g');

/**
 * Reverses {@link conditionalsToBlocks}: turns each labelled box back into its
 * `{{#if}}…{{else}}…{{/if}}` Handlebars. Runs innermost-first (repeatedly) so
 * nested conditionals unwrap correctly.
 */
export function blocksToConditionals(html: string): string {
  if (!html || !html.includes(COND_OPEN_ATTR)) return html;

  let prev: string;
  let current = html;
  do {
    prev = current;
    current = current.replace(
      COND_BLOCK_RE,
      (_whole, openTok: string, inner: string, closeTok: string) => {
        // Strip the structural label and body wrappers; keep their contents.
        let body = inner
          .replace(COND_LABEL_DIV_RE, '')
          .replace(COND_ELSE_DIV_RE, (_m, elseTok: string) => decodeHtml(elseTok))
          .replace(COND_BODY_OPEN_RE, '')
          .replace(/<\/div>/g, '');
        body = body.trim();
        return `${decodeHtml(openTok)}${body}${decodeHtml(closeTok)}`;
      },
    );
  } while (current !== prev);

  return current;
}
